import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { getAuth } from "firebase/auth";
import MaterialEditor from "../components/MaterialEditor";
import ProjectTabs from "../components/ProjectTabs";

const organizeFilesByFolder = (files) => {
  const structure = {};

  Object.entries(files).forEach(([fullPath, fileList]) => {
    const parts = fullPath.split("/");
    if (parts.length < 2) return;

    const [mainFolder, subFolder] = parts;

    if (!structure[mainFolder]) structure[mainFolder] = {};
    if (!structure[mainFolder][subFolder]) structure[mainFolder][subFolder] = [];

    structure[mainFolder][subFolder].push(...fileList);
  });

  return structure;
};

const ProjectDocsPage = () => {
  const { projectId } = useParams();
  const [files, setFiles] = useState({});
  const [recipients, setRecipients] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    const userId = user?.uid;

    const fetchDocs = async () => {
      try {
        const res = await axios.get(
          `https://modularobjects.com/checkout/api/project-files/list-all-project/${userId}/${projectId}`
        );
        setFiles(res.data);
      } catch (err) {
        console.error("❌ Greska:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchUsers = async () => {
      try {
        const res = await axios.get("https://modularobjects.com/checkout/api/user/list");
        setRecipients(res.data);
      } catch (err) {
        console.error("❌ Ne mogu učitati korisnike:", err);
      }
    };

    fetchDocs();
    fetchUsers();
  }, [projectId]);

  useEffect(() => {
    if (!activeFolder && Object.keys(files).length > 0) {
      const folderNames = Object.keys(files);
      const latest = folderNames.sort().reverse()[0];
      setActiveFolder(latest);
    }
  }, [files]);

  const handleSend = async () => {
    try {
      await axios.post("https://modularobjects.com/checkout/api/project-files/send-docs", {
        projectId,
        recipients: selectedEmails,
      });
      alert("📤 Dokumentacija je poslata!");
    } catch (err) {
      alert("❌ Greška pri slanju dokumentacije.");
    }
  };

  const getLatestTimestampFromFiles = (files) => {
    const allFilenames = Object.values(files).flat().map((f) => f.filename);
    const jsonFiles = allFilenames.filter(
      (name) => name.startsWith("place_holder_materials_") && name.endsWith(".json")
    );
    const timestamps = jsonFiles.map((name) =>
      name.replace("place_holder_materials_", "").replace(".json", "")
    );
    return timestamps.sort().reverse()[0];
  };

  const aggregateStatus = (fileList) => {
    const allSent = new Set();
    const allOpened = new Set();
    const allDownloaded = new Set();
    const allEdited = new Set();

    for (const f of fileList) {
      f.status?.sentTo?.forEach((email) => allSent.add(email));
      f.status?.openedBy?.forEach((email) => allOpened.add(email));
      f.status?.downloadedBy?.forEach((email) => allDownloaded.add(email));
      f.status?.editedBy?.forEach((email) => allEdited.add(email));
    }

    return {
      sentTo: [...allSent],
      openedBy: [...allOpened],
      downloadedBy: [...allDownloaded],
      editedBy: [...allEdited],
    };
  };
  

  return (
  <div className="p-6 max-w-5xl mx-auto">
    <ProjectTabs active="docs" />
    <h1 className="text-2xl font-bold mb-4">📁 Dokumentacija za projekat</h1>

    {loading ? (
      <p>Učitavanje...</p>
    ) : (
      Object.entries(organizeFilesByFolder(files)).map(([mainFolder, subfolders]) => (
        <div key={mainFolder} className="mb-10 border rounded p-4 shadow">
          <h2 className="font-bold text-xl mb-3">📁 {mainFolder}</h2>

          {Object.entries(subfolders).map(([subFolder, fileList]) => {
            const grouped = fileList.reduce((acc, file) => {
              const ext = file.filename.split(".").pop().toUpperCase();
              if (!acc[ext]) acc[ext] = [];
              acc[ext].push(file);
              return acc;
            }, {});

            const status = aggregateStatus(fileList);

            return (
              <div key={subFolder} className="ml-4 mb-6">
                <h3 className="text-md font-semibold mb-2">📁 {subFolder}</h3>
                <div className="text-sm text-gray-600 mb-2">
                  📤 Poslato: {status.sentTo.join(", ") || "nije"}
                  {" | "}👁️ Otvoreno: {status.openedBy.join(", ") || "nije"}
                  {" | "}⬇️ Skinuto: {status.downloadedBy.join(", ") || "nije"}
                  {" | "}✏️ Izmenjeno: {status.editedBy.join(", ") || "nije"}
                </div>

                {Object.entries(grouped).map(([type, files]) => (
                  <div key={type} className="ml-4 mb-2">
                    <h4 className="text-sm font-semibold mb-1">📄 {type}</h4>
                    <ul className="ml-4 list-disc space-y-1 text-blue-600">
                      {files.map((f, i) => (
                        <li key={i}>
                          <a
                            href={f.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            {f.filename}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ))
    )}

    {/* SEKCIJA ZA SLANJE */}
    <div className="mt-8">
      <h2 className="text-lg font-semibold mb-2">📧 Pošalji dokumentaciju</h2>
      <select
        multiple
        className="w-full border rounded p-2"
        onChange={(e) =>
          setSelectedEmails(
            Array.from(e.target.selectedOptions, (option) => option.value)
          )
        }
      >
        {recipients.map((user) => (
          <option key={user.email} value={user.email}>
            {user.name} ({user.email})
          </option>
        ))}
      </select>
      <button
        onClick={handleSend}
        className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
      >
        📩 Pošalji
      </button>
    </div>

    {/* SEKCIJA ZA MATERIJALE */}
    <div className="mt-12">
      <h2 className="text-2xl font-bold mb-4">🧱 Uredi materijale za izveštaj</h2>
      <MaterialEditor
        projectId={projectId}
        timestamp={getLatestTimestampFromFiles(files)}
      />
    </div>
  </div>
);
};

export default ProjectDocsPage;
